<template>
    <div class="flex w-full flex-col">
        <h3 v-if="showTitle" class="font-serif text-valence-grey-850">{{ title }}</h3>
        <hr v-if="showTitle" class="border-valence-grey-250 mt-2 mb-3" />
        <div class="flex-1" :class="gridView ? 'grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-fr' : 'flex flex-col gap-2.5'">
            <ChatUseCaseEntrance
                v-for="(useCase, index) in orderedUseCases"
                :key="useCase.call_code"
                :use-case="useCase"
                :icon="tileIcons[index]"
                :location="props.location"
                :variant="variant"
                @insight-hover="emit('insight-hover', $event)"
                @insight-hover-end="emit('insight-hover-end')"
                @insight-clicked="emit('insight-clicked', $event)" />
        </div>
    </div>
</template>

<script setup lang="ts">
import TileGreen from "~vue/icons/tiles/TileGreen.vue";
import TileOrange from "~vue/icons/tiles/TileOrange.vue";
import TilePink from "~vue/icons/tiles/TilePink.vue";
import TileTeal from "~vue/icons/tiles/TileTeal.vue";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed, onMounted } from "vue";

import ChatUseCaseEntrance from "./ChatUseCaseEntrance.vue";
import { ChatUseCaseDisplayVariant, ChatUseCaseLocation, makeUseCasesShownLogData } from "./utils";

interface Props {
    useCases: ChatEntranceUseCaseData[];
    location: ChatUseCaseLocation;
    showTitle?: boolean;
    title?: string;
    gridView?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
    showTitle: true,
    title: "How can I help you today?",
    gridView: false,
});

const variant = ChatUseCaseDisplayVariant.CARD;
const tileIcons: Component[] = [TilePink, TileOrange, TileTeal, TileGreen];

const emit = defineEmits<{
    (e: "insight-hover", useCase: ChatEntranceUseCaseData): void;
    (e: "insight-hover-end"): void;
    (e: "insight-clicked", useCase: ChatEntranceUseCaseData): void;
}>();

const orderedUseCases = computed(() =>
    [...props.useCases].sort((a, b) => {
        // == operator checks if position is null or undefined
        // Use cases with non-defined position should always be at the end of the list
        if (a.position == null) {
            return 1;
        }
        if (b.position == null) {
            return -1;
        }
        return a.position - b.position;
    }),
);

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.CHAT_USE_CASE_ENTRANCES_SHOWN, makeUseCasesShownLogData(variant, props.location, props.useCases));
});
</script>
